import { useCallback, useEffect, useRef, useState } from 'react';
import { inSpeedRun, readRunOrb, writeRunOrb } from '@/lib/speedRun';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ORB_FILL_EVENT, fillValueText, requestFill, type FillChange } from '@/lib/orbFill.core';
import { assistantApi, communityApi, type OrbNudge, type OrbNoticeState } from '@/api/client';
import { OrbNoticeCard } from '@/components/OrbNoticeCard';
import { useQuery } from '@tanstack/react-query';
import { OrbMessagesWeb } from '@/components/community/OrbMessagesWeb';
import { useSyncedPreference } from '@/hooks/useSyncedPreference';
import { PaperPlaneRight, ArrowRight, ThumbsUp, ThumbsDown } from '@phosphor-icons/react';
import { AnswerText } from '@/components/AnswerText';
import { GUIDES, ORB_GUIDE_EVENT } from '@/components/orbGuides';
import { track } from '@/lib/analytics';
import { PERSONAL_KEYS } from '@/lib/browserState';
import { isViewingAsSomeoneElse } from '@/lib/viewAs';
import { useAuth } from '@/contexts/AuthContext';
import { useUiMode } from '@/hooks/useUiMode';
import { orbDailyQuestions } from '@/lib/tiers';
import { PLANNED_LANGUAGES } from '@/i18n';
import { appLanguage } from '@/lib/format';
import { orbScreenDetail } from './orbScreenDetail';
import { orbIntentLine } from '@/lib/onboarding';
import { stuckOfferFor } from '@/lib/stuck.core';

/** The app's language, said to the Orb so it can answer in it (2026-09-27). */
function languageLine(): string {
  const code = appLanguage();
  const l = PLANNED_LANGUAGES.find((x) => x.code === code);
  return l ? ` Their app is set to ${l.name} (${code}).` : '';
}

/****************
 * The Orb's chat panel, on the web.
 *
 * Guillaume, 2026-09-04: "the Orb chatbox is connected to the mobile version but
 * not the web." It was not a connection that was missing — there was no chat
 * here at all. The web Orb was a popover cycling canned tips by route, which is
 * a different product from the one the phone has.
 *
 * ── Same contract as the mobile panel ───────────────────────────────────────
 *
 * Transcript, input, typing indicator, and a backend that holds the model. The
 * mobile version was built so that only its `answer()` changed when a real model
 * arrived; this one starts where that ended up, calling the same endpoint with
 * the same shape, so the two clients ask one assistant rather than two.
 *
 * ── Why the tips did not survive ────────────────────────────────────────────
 *
 * A route-keyed tip and an assistant that can answer the question the tip was
 * gesturing at are not complementary. Keeping both would put a static sentence
 * about the Live Prices panel next to a box that will explain it properly, and
 * the reader has to work out which one is worth reading. The route still travels
 * — as CONTEXT on the question — so the answer can be about the screen in front
 * of them, which is what the tips were reaching for.
 *
 * ── Failure is a message, not a spinner that never stops ────────────────────
 *
 * Three things can go wrong and they are different: no model is configured, the
 * model could not be reached, or too many questions were asked. Each says so in
 * its own words. The one thing this never does is leave a question looking like
 * it was received when it was not.
 ****************/

const SIZE_KEY = 'copyfi.orb.size';

type Corner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

/** Which way each corner's cursor points, so the affordance reads before it is
 *  grabbed. */
const CORNER_STYLE: Record<Corner, string> = {
  'top-left': 'top-0 left-0 cursor-nwse-resize',
  'top-right': 'top-0 right-0 cursor-nesw-resize',
  'bottom-left': 'bottom-0 left-0 cursor-nesw-resize',
  'bottom-right': 'bottom-0 right-0 cursor-nwse-resize',
};

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

/****************
 * The conversation follows the person, not the device.
 *
 * Guillaume, 2026-09-04: "Can we also sync convo for mobile and web?"
 *
 * The same store the watchlist and the column choice already use, so a question
 * asked on the phone is still there on the laptop. That is the right shape for a
 * support conversation: someone asks why an order was skipped on their phone,
 * opens the dashboard to look, and should not have to explain themselves twice.
 *
 * ── Why it is trimmed ───────────────────────────────────────────────────────
 *
 * A preference value is capped at 16KB server-side, and a transcript is the only
 * one of these that grows without limit. Keeping the last few exchanges is also
 * the useful part -- nobody returns to a support chat for its twentieth turn --
 * so the oldest fall off rather than the whole thing failing to save once it
 * outgrows the cap.
 ****************/
const CONVO_KEY = PERSONAL_KEYS[0]; // 'copyfi.orb.conversation', listed in lib/browserState
const CONVO_KEEP = 12;

interface Turn {
  role: 'user' | 'assistant';
  content: string;
  /** A spotlight sequence the Orb offered to run for this answer. Validated by
   *  the backend against its own list before it ever gets here, and validated
   *  again by the tour before anything dims. Absent on most answers. */
  guide?: string;
  /** Strategy fields the Orb offers to fill in; applied only when the user presses the button (2026-10-24). */
  fill?: FillChange[];
  /** Names this answer for the person's thumbs (assistant_learning.go, 2026-10-22). */
  ref?: string;
  rating?: 1 | -1;
  /** Asked for a person after a thumbs down (10-31). */
  handedOver?: boolean;
  /** The Orb spoke first (a live message or the daily one): its trigger, for "Did this help?" (10-25). */
  nudgeOf?: string;
  /** The answer to it: yes, or the reason it did not. */
  helped?: 'yes' | 'no' | string;
}

/** What screen the question was asked from. The backend labels this as data. */
const SCREEN_NAMES: Record<string, string> = {
  '/': 'the Dashboard',
  '/traders': 'a trader profile',
  '/copy-trade': 'the Copy Trade screen, starting a session',
  '/configure': 'the Strategy Builder',
  '/sessions': 'their sessions list',
  '/session': 'a session detail page',
  '/ai': 'the AI connections page',
  '/community': 'the Community',
  '/learn': 'the Learn page',
};

function screenFor(pathname: string): string {
  const key = Object.keys(SCREEN_NAMES).find((k) =>
    k === '/' ? pathname === '/' : pathname.startsWith(k),
  );
  return SCREEN_NAMES[key || '/'];
}

export function OrbChat({ onClose, nudge, onNudgeShown, interview = false, checkIn = null, onInterviewEnd, onUpdateProfile, onCheckInDue, ask, askGuide, onAsked }: {
  onClose: () => void;
  /** A question a "Why?" link sent (orbLive.ts askOrb): asked as soon as the chat can. */
  ask?: string | null;
  /** A Show me to offer under the answer to `ask` when the model names none. */
  askGuide?: string;
  onAsked?: () => void;
  /** Something the Orb raises on its own; shown as its opening message. */
  nudge?: OrbNudge | null;
  onNudgeShown?: () => void;
  /** The first conversation: answers go to the interview until it closes. */
  interview?: boolean;
  /** Why a check-in is happening (time, self, or a change in what they do), or null for the first conversation. */
  checkIn?: string | null;
  /** done: the conversation closed (false = "Not now"). */
  onInterviewEnd?: (done: boolean) => void;
  /** "Update my profile": only offered once a profile exists. */
  onUpdateProfile?: () => void;
  /** The person asked for it in words ("update my profile"): the chat said a check-in is now due. */
  onCheckInDue?: () => void;
}) {
  const location = useLocation();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const viewingAs = isViewingAsSomeoneElse();
  // Free and Telegram get a daily number of questions (lib/tiers.ts); not
  // counted yet, so this only says so.
  const { tier, can, isRealAdmin, tierLens } = useAuth();
  const { mode: uiMode } = useUiMode();
  // Their plan and app mode, which live on the account, not in the server's briefing (10-25).
  // An admin's plan is not "free" because they preview the app through a lens (10-30: the
  // founder was told the Orb had no way to know who he is). The server says it too (assistant_lookups.go).
  const planWords = isRealAdmin
    ? (tierLens && tierLens !== 'admin' ? `CopyFi admin, previewing the app as the ${tierLens} plan` : 'CopyFi admin')
    : (tier ?? 'unknown');
  const accountLine = ` Their plan: ${planWords}. App mode: ${uiMode === 'lite' ? 'Lite' : 'Pro'}.`;
  const orbLimited = !can('orb_unlimited');
  const orbQuota = tier ? orbDailyQuestions(tier) : null;
  const [syncedTurns, setSyncedTurns] = useSyncedPreference<Turn[]>(CONVO_KEY, []);
  // An admin's speed run talks to a fresh Orb: its own turns, in this browser (lib/speedRun.ts).
  const inRun = inSpeedRun();
  const [runTurns, setRunTurnsState] = useState<Turn[]>(readRunOrb as () => Turn[]);
  const setRunTurns = useCallback((u: Turn[] | ((prev: Turn[]) => Turn[])) => {
    setRunTurnsState((prev) => {
      const next = typeof u === 'function' ? (u as (p: Turn[]) => Turn[])(prev) : u;
      writeRunOrb(next);
      return next;
    });
  }, []);
  const turns = inRun ? runTurns : syncedTurns;
  const setTurns = (inRun ? setRunTurns : setSyncedTurns) as typeof setSyncedTurns;
  const [draft, setDraft] = useState('');
  // The Orb and the member's Messages share the panel (live review #140).
  const [pane, setPane] = useState<'orb' | 'messages'>('orb');
  const chatList = useQuery({ queryKey: ['community', 'chat'], queryFn: communityApi.chatList, refetchInterval: 30_000, enabled: !isViewingAsSomeoneElse() });
  const unreadChats = (chatList.data?.chats ?? []).reduce((n, c) => n + (c.unread || 0), 0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [available, setAvailable] = useState<boolean | null>(null);
  // How the one notice is sent (OrbNoticeCard): asked once, changeable from
  // the header. Kept on screen after the answer so its confirmation is read.
  const [notice, setNotice] = useState<OrbNoticeState | null>(null);
  const [noticeOpen, setNoticeOpen] = useState(false);
  useEffect(() => {
    if (interview || viewingAs) return;
    let live = true;
    // Through a promise: a missing or throwing call leaves the chat working (10-31).
    Promise.resolve().then(() => assistantApi.notice()).then((s) => {
      if (!live) return;
      setNotice(s);
      if (s.ask) setNoticeOpen(true);
    }).catch(() => { /* the question waits for the next open */ });
    return () => { live = false; };
  }, [interview, viewingAs]);

  const scroller = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  /*
   * The panel's size, chosen by the user.
   *
   * Guillaume, 2026-09-04: "got no way for me to change the shape of the
   * chatbox so please make it adjustable." The phone panel has had corner
   * resizing since it shipped; the web one was a fixed 380px box.
   *
   * localStorage rather than the synced preference store, deliberately: a
   * comfortable size on a 27-inch monitor is not a comfortable size on a phone
   * browser, so this is one of the few settings that SHOULD stay per-device.
   * The conversation itself does travel -- that is about the person, not the
   * screen.
   */
  const [size, setSize] = useState<{ w: number; h: number }>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(SIZE_KEY) || 'null');
      if (saved && typeof saved.w === 'number' && typeof saved.h === 'number') return saved;
    } catch { /* a corrupt entry is not worth a crash */ }
    return { w: 380, h: 420 };
  });
  /*
   * Resizing, from any corner.
   *
   * Guillaume, 2026-09-04: "Make it adjustable from all 4 corners." One corner
   * only works if the panel happens to be somewhere convenient, and this one is
   * dragged around the screen by the orb -- against the right edge the handle
   * that grows it rightward is the one you cannot reach.
   *
   * Each corner has its own arithmetic because the panel is anchored at its
   * BOTTOM-LEFT, above the orb. Growing left or down means moving the anchor,
   * which the popover owns, so those directions grow the box away from the
   * anchor instead: dragging the top-left handle left widens it, dragging up
   * heightens it. The rule that makes all four feel right is that the corner
   * under the cursor follows the cursor.
   */
  const resizing = useRef<{ x: number; y: number; w: number; h: number; corner: Corner } | null>(null);

  const onResizeDown = (corner: Corner) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizing.current = { x: e.clientX, y: e.clientY, w: size.w, h: size.h, corner };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onResizeMove = (e: React.PointerEvent) => {
    const from = resizing.current;
    if (!from) return;
    const dx = e.clientX - from.x;
    const dy = e.clientY - from.y;

    // A right-hand corner grows with rightward travel; a left-hand one grows
    // with leftward travel. Same for up and down.
    const growX = from.corner.includes('right') ? dx : -dx;
    const growY = from.corner.startsWith('top') ? -dy : dy;

    setSize({
      w: clamp(from.w + growX, 300, Math.min(720, window.innerWidth - 40)),
      h: clamp(from.h + growY, 240, Math.min(760, window.innerHeight - 120)),
    });
  };

  const onResizeUp = () => {
    if (!resizing.current) return;
    resizing.current = null;
    try { localStorage.setItem(SIZE_KEY, JSON.stringify(size)); } catch { /* ignore */ }
  };

  /*
   * The server's copy of the conversation, read each time the panel opens.
   *
   * Asked 2026-10-09: start on the web, continue on the phone. Every exchange is
   * already logged server-side, so that log is the conversation and the synced
   * preference only the cache that paints first. Not during the interview
   * (its opening line is local), and not once the user has typed: their own
   * question beats a read that was in flight. A guide offered on this device is
   * kept by matching the answer it came with; a nudge not yet answered stays.
   */
  const askedSinceOpen = useRef(false);
  useEffect(() => {
    if (interview) return;
    let alive = true;
    assistantApi.conversation().then(({ turns: server }) => {
      if (!alive || askedSinceOpen.current || !Array.isArray(server)) return;
      setTurns((local) => {
        const guides = new Map(local.filter((x) => x.guide).map((x) => [x.content, x.guide]));
        const merged: Turn[] = server.map((x) => (guides.has(x.content) ? { ...x, guide: guides.get(x.content) } : x));
        if (nudge && !merged.some((x) => x.content === nudge.message)) {
          merged.push({ role: 'assistant' as const, content: nudge.message, guide: nudge.guide, nudgeOf: nudge.trigger ?? nudge.kind });
        }
        return merged.slice(-CONVO_KEEP);
      });
    }).catch(() => { /* offline or signed out: the cached copy stands */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per opening
  }, [interview]);

  /*
   * The Orb's own opening line, when it has one.
   *
   * Put into the transcript as an assistant turn, so the conversation reads
   * the way it happened -- the Orb spoke first -- and the model sees it as
   * history when the user answers. Marked seen once shown, so it is said once:
   * the backend brings it back after a week if the problem is still there.
   */
  useEffect(() => {
    if (!nudge) return;
    if (!turns.some((t) => t.role === 'assistant' && t.content === nudge.message)) {
      // The guide rides along, so an unprompted message can be SHOWN and not
      // only read. Someone the daily check found stuck is exactly the person
      // who has already failed to act on a sentence.
      setTurns([...turns, { role: 'assistant' as const, content: nudge.message, guide: nudge.guide, nudgeOf: nudge.trigger ?? nudge.kind }]
        .slice(-CONVO_KEEP));
    }
    track('assistant_nudge_shown', nudge.kind);
    // A LIVE message (orbLive.ts) was never stored on the server: nothing to mark.
    if (nudge.id > 0) {
      assistantApi.nudgeSeen(nudge.id).catch(() => { /* shown anyway; it may come back next check */ });
    }
    onNudgeShown?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per nudge, not per transcript change
  }, [nudge?.id]);

  /*
   * The first conversation opens with the Orb's own line, in the app's
   * language (common.json orb.interview.opening): no model call to say hello.
   * Not repeated when they come back to an interview left mid-way.
   */
  useEffect(() => {
    if (!interview) return;
    // A check-in opens by why it is happening; the model then picks up from
    // what it already knows about them.
    const opening = checkIn
      ? t(checkIn === 'time' ? 'orb.checkin.opening.time' : checkIn === 'self' ? 'orb.checkin.opening.self' : 'orb.checkin.opening.changed')
      : t('orb.interview.opening');
    // Only the LAST message: a check-in months later may reuse a line that is
    // still further up the transcript.
    if (turns[turns.length - 1]?.content !== opening) {
      setTurns([...turns, { role: 'assistant' as const, content: opening }].slice(-CONVO_KEEP));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once when the interview starts
  }, [interview, checkIn]);

  const notNow = () => {
    assistantApi.interview('', [], undefined, 'skip').catch(() => { /* it will simply be offered again */ });
    track('orb_interview_skipped', String(turns.filter((x) => x.role === 'user').length));
    onInterviewEnd?.(false);
  };

  // Ask once whether there is a model at all. An input that accepts a question
  // and then always fails is worse than one that says the assistant is off.
  useEffect(() => {
    let alive = true;
    assistantApi
      .status()
      .then((s) => { if (alive) setAvailable(s.available); })
      .catch(() => { if (alive) setAvailable(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
  }, [available]);

  // A "Why?" link's question, once the chat is ready to answer.
  useEffect(() => {
    if (!ask || !available || sending) return;
    onAsked?.();
    void send(ask, askGuide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ask, available]);

  // Follow the conversation down as it grows.
  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' });
  }, [turns, sending]);

  const send = async (override?: string, fallbackGuide?: string) => {
    const question = (override ?? draft).trim();
    if (!question || sending) return;

    setDraft('');
    setError(null);
    // The question goes up straight away. Waiting for the answer to render both
    // makes the app look frozen for the seconds a model takes.
    // `as const` so the literal stays 'user' rather than widening to string:
    // Turn's role is a union of two literals, and without this the array is
    // { role: string }[] and assigns to nothing.
    const asked: Turn[] = [...turns, { role: 'user' as const, content: question }].slice(-CONVO_KEEP);
    askedSinceOpen.current = true;
    setTurns(asked);
    setSending(true);
    // Counted here rather than on the answer: a question that fails is still a
    // question they had, and those are the ones worth reading.
    track('assistant_asked', question.slice(0, 300));

    try {
      // Only the transcript so far, never the pending question twice. Sent as
      // role and content alone: a stored `guide` is ours, not part of what was
      // said, and feeding old marker ids back to the model invites it to repeat
      // them for questions that did not ask for one.
      const history = turns.map(({ role, content }) => ({ role, content }));
      // A screen that knows more than its route says so (orbScreenDetail.ts).
      const where = orbScreenDetail() || screenFor(location.pathname);
      if (interview) {
        const res = await assistantApi.interview(question, history, `The user is on ${where}.${orbIntentLine()}${languageLine()}`);
        // A real question is answered by the Orb itself, with everything about
        // their account (assistant_profile.go, 2026-10-22), and it ends the
        // getting-to-know-you conversation for good (Guillaume 2026-10-24: "if I
        // ask him questions, he should just answer and stop asking").
        if (res.route === 'chat') onInterviewEnd?.(false);
        if (res.route !== 'chat') {
          setTurns([...asked, { role: 'assistant' as const, content: res.answer ?? '' }].slice(-CONVO_KEEP));
          if (res.done) {
            track('orb_interview_done', String(history.length));
            onInterviewEnd?.(true);
          }
          return;
        }
      }
      const res = await assistantApi.ask(question, history, `The user is on ${where}.${orbIntentLine()}${languageLine()}${accountLine}`);
      setTurns([...asked, { role: 'assistant' as const, content: res.answer, guide: res.guide || fallbackGuide, fill: res.fill, ref: res.ref }].slice(-CONVO_KEEP));
      // "Update my profile" typed rather than pressed: the server made the
      // check-in due, so the catch-up opens now, not on the next visit. An
      // absent field is false.
      if (res.checkin_due === true) onCheckInDue?.();
    } catch (e: any) {
      // The question stays on screen. Losing what they typed alongside the
      // failure is two problems instead of one.
      const status = e?.status ?? e?.response?.status;
      // 503 has two meanings and the server's own sentence tells them apart:
      // no model is configured at all, or the model host's account has used
      // its allowance (2026-10-02: "try again in a moment" was shown for a
      // whole evening, and could not have worked until the monthly reset).
      const unavailable = status === 503 && /unavailable for now/i.test(String(e?.message ?? ''));
      setError(
        status === 429 && interview
          ? t('orb.interview.later')
          : status === 429
          ? t('orb.chat.error.rateLimited')
          : unavailable
            ? t('orb.chat.error.unavailable')
            : status === 503
              ? t('orb.chat.error.noModel')
              : t('orb.chat.error.failed'),
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="relative flex flex-col rounded-xl overflow-hidden glass-card"
      style={{ width: size.w, height: size.h, maxWidth: 'calc(100vw - 2rem)', maxHeight: 'calc(100vh - 8rem)' }}
    >
      {/* One handle per corner. 14px of padding around a small mark, because a
          resize target that needs aiming at is a resize target nobody uses. */}
      {(Object.keys(CORNER_STYLE) as Corner[]).map((corner) => (
        <div
          key={corner}
          onPointerDown={onResizeDown(corner)}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeUp}
          onPointerCancel={onResizeUp}
          className={`absolute z-10 p-2 touch-none ${CORNER_STYLE[corner]}`}
          aria-label={t(`orb.chat.resize.${corner}`)}
          role="separator"
        >
          <span className="block h-2 w-2 rounded-sm border border-current opacity-25" />
        </div>
      ))}

      <div className="px-4 py-3 border-b border-white/[0.06] flex items-center gap-2">
        <img src="/copyfilogo.png" alt="" className="h-5 w-5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-none">{t('orb.assistantLabel')}</p>
          <p className="text-[11px] text-muted-foreground mt-1">
            {interview ? (checkIn ? t('orb.checkin.subtitle') : t('orb.interview.subtitle')) : t('orb.chat.subtitle')}
          </p>
          {orbLimited && (
            <p className="text-[10px] text-warning mt-1">
              {orbQuota !== null ? t('tier.orb.limited', { count: orbQuota }) : t('tier.orb.limitedUnset')}
            </p>
          )}
        </div>
        {interview && (
          <button
            onClick={notNow}
            className="text-[11px] text-muted-foreground hover:text-foreground px-1"
          >
            {t('orb.interview.notNow')}
          </button>
        )}
        {!interview && onUpdateProfile && (
          <button
            onClick={onUpdateProfile}
            className="text-[11px] text-muted-foreground hover:text-foreground px-1"
          >
            {t('orb.profile.update')}
          </button>
        )}
        {/* No Clear button (live review #140, Guillaume 10-26). */}
        {!interview && !viewingAs && notice?.session_id && !notice.ask && (
          <button
            onClick={() => setNoticeOpen((o) => !o)}
            className="text-[11px] text-muted-foreground hover:text-foreground px-1"
          >
            {t('orb.notice.change')}
          </button>
        )}
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground text-lg leading-none px-1"
          aria-label={t('orb.chat.closeLabel')}
        >
          ×
        </button>
      </div>

      {/* Two tabs, always visible: the Orb and your Messages with other members
          (live review #140: "switching to Messages is not obvious enough"). */}
      {!interview && chatList.data?.member && (
        <div className="flex border-b border-white/[0.06]" role="tablist">
          {(['orb', 'messages'] as const).map((k) => (
            <button key={k} type="button" role="tab" aria-selected={pane === k} onClick={() => setPane(k)}
              className={`flex-1 py-2 text-xs font-medium border-b-2 ${pane === k ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
              {k === 'orb' ? t('orb.assistantLabel') : `${t('community:chat.title')}${unreadChats ? ` (${unreadChats})` : ''}`}
            </button>
          ))}
        </div>
      )}
      {pane === 'messages' ? <OrbMessagesWeb /> : (<>
      <div ref={scroller} className="flex-1 px-4 py-3 space-y-3 overflow-y-auto min-h-0">
        {available === false && (
          <p className="text-xs text-muted-foreground">
            {t('orb.chat.unavailable')}
          </p>
        )}

        {available !== false && turns.length === 0 && (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {t('orb.chat.empty')}
            </p>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {[
                t('orb.chat.suggest1'),
                t('orb.chat.suggest2'),
                t('orb.chat.suggest3'),
              ].map((q) => (
                <button
                  key={q}
                  onClick={() => { setDraft(q); inputRef.current?.focus(); }}
                  className="text-[11px] px-2 py-1 rounded-md border border-white/10 text-muted-foreground hover:text-foreground hover:border-white/20 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {turns.map((turn, i) => (
          <div key={i} className={turn.role === 'user' ? 'flex flex-col items-end' : 'flex flex-col items-start'}>
            <div
              className={`rounded-lg px-3 py-2 text-sm max-w-[85%] whitespace-pre-wrap ${
                turn.role === 'user'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white/[0.04] text-foreground'
              }`}
            >
              {turn.role === 'assistant' ? <AnswerText text={turn.content} /> : turn.content}
            </div>
            {/****************
              * "Show me" — the answer, in the interface itself.
              *
              * Offered, never automatic. A screen that dims itself because you
              * asked a question is an interface acting on you; a button is the
              * same help with the person still driving. Guillaume's own rule
              * for this feature was that it must always be dismissible.
              *
              * Closing the chat is not a flourish: this panel sits over the
              * page, so leaving it open would cover the control the spotlight
              * is about to ring.
              *
              * The id is checked against GUIDES here too. The button exists to
              * do something, and offering one that would dim the screen over
              * nothing is worse than not offering it.
              ****************/}
            {turn.role === 'assistant' && turn.guide && Object.prototype.hasOwnProperty.call(GUIDES, turn.guide) && (
              <button
                type="button"
                className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/[0.06] px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/[0.12] transition-colors"
                onClick={() => {
                  const guide = turn.guide as string;
                  track('assistant_guide_started', guide);
                  onClose();
                  window.dispatchEvent(new CustomEvent(ORB_GUIDE_EVENT, { detail: { guide } }));
                }}
              >
                {t('orb.chat.showMe')}
                <ArrowRight className="h-3 w-3 rtl:-scale-x-100" />
              </button>
            )}
            {/* Fill it in (assistant_fill.go, 2026-10-24): the Orb's offer, applied to the
                Strategy Builder's form only when pressed; saving stays theirs. */}
            {turn.role === 'assistant' && turn.fill && turn.fill.length > 0 && (
              <button
                type="button"
                className="mt-1.5 ms-1.5 inline-flex flex-wrap items-center gap-1.5 rounded-md border border-primary/30 bg-primary/[0.06] px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/[0.12] transition-colors text-start"
                onClick={() => {
                  const changes = turn.fill as FillChange[];
                  track('assistant_fill_applied', changes.map((c) => c.field).join(','));
                  const open = requestFill(changes, (c) => window.dispatchEvent(new CustomEvent(ORB_FILL_EVENT, { detail: { changes: c } })));
                  if (!open) navigate('/configure');
                }}
              >
                {t('orb.fill.button')}: {(turn.fill as FillChange[]).map((c) => `${t(`orb.fill.field.${c.field}`)} ${t(`orb.fill.value.${c.value}`, { defaultValue: fillValueText(c) })}`).join(', ')}
              </button>
            )}
            {/* Did this help? (Guillaume 10-25: "a nudge that changed nothing ->
                improve it by understanding why"). Under the Orb's own messages;
                a "no" asks why, from fixed choices (never typed text). */}
            {turn.role === 'assistant' && turn.nudgeOf === 'builder-abandoned' && !turn.ref && (
              <AbandonReasons turn={turn} onAnswer={(helped) => setTurns((cur) => cur.map((x) => (x === turn ? { ...x, helped } : x)))} />
            )}
            {turn.role === 'assistant' && turn.nudgeOf && turn.nudgeOf !== 'builder-abandoned' && !turn.ref && (
              <NudgeHelped turn={turn} onAnswer={(helped) => setTurns((cur) => cur.map((x) => (x === turn ? { ...x, helped } : x)))} />
            )}
            {/* Was this helpful? (assistant_learning.go, 2026-10-22): every answer
                the model gave can be rated; a thumbs down goes to review. */}
            {turn.role === 'assistant' && turn.ref && (
              <div className="mt-1 flex items-center gap-1" role="group" aria-label={t('orb.chat.rateLabel')}>
                {([1, -1] as const).map((v) => (
                  <button key={v} type="button" aria-pressed={turn.rating === v}
                    aria-label={v > 0 ? t('orb.chat.rateUp') : t('orb.chat.rateDown')}
                    disabled={!!turn.rating}
                    className={`p-1 rounded hover:bg-white/[0.06] transition-colors ${turn.rating === v ? 'text-primary' : 'text-muted-foreground/60'} disabled:hover:bg-transparent`}
                    onClick={() => {
                      const ref = turn.ref as string;
                      setTurns((cur) => cur.map((x) => (x.ref === ref ? { ...x, rating: v } : x)));
                      track(v > 0 ? 'assistant_rated_up' : 'assistant_rated_down');
                      void assistantApi.rate(ref, v).catch(() => undefined);
                    }}>
                    {v > 0 ? <ThumbsUp className="h-3.5 w-3.5" weight={turn.rating === v ? 'fill' : 'regular'} />
                      : <ThumbsDown className="h-3.5 w-3.5" weight={turn.rating === v ? 'fill' : 'regular'} />}
                  </button>
                ))}
                {turn.rating ? <span className="text-[11px] text-muted-foreground">{t('orb.chat.rateThanks')}</span> : null}
              </div>
            )}
            {/* Still stuck (Guillaume, 10-31): after a thumbs down, the next step --
                the guide for this screen, the answer again more simply, or a person. */}
            {turn.role === 'assistant' && turn.ref && turn.rating === -1 && (
              <StillStuck
                guide={stuckOfferFor(location.pathname)?.guide ?? null}
                handedOver={!!turn.handedOver}
                onGuide={(guide) => { track('assistant_stuck_guide', guide); onClose(); window.dispatchEvent(new CustomEvent(ORB_GUIDE_EVENT, { detail: { guide } })); }}
                onSimpler={() => { track('assistant_stuck_simpler'); void send(t('orb.stuck.simplerAsk')); }}
                onPerson={() => {
                  const ref = turn.ref as string;
                  track('assistant_stuck_person');
                  setTurns((cur) => cur.map((x) => (x.ref === ref ? { ...x, handedOver: true } : x)));
                  void assistantApi.stillStuck(ref).catch(() => undefined);
                }}
              />
            )}
          </div>
        ))}

        {!interview && !viewingAs && notice && noticeOpen && (
          <OrbNoticeCard state={notice} onChanged={setNotice} />
        )}

        {sending && (
          <div className="flex justify-start">
            <div className="rounded-lg px-3 py-2 bg-white/[0.04]">
              <span className="inline-flex gap-1">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60 animate-pulse"
                    style={{ animationDelay: `${i * 150}ms` }}
                  />
                ))}
              </span>
            </div>
          </div>
        )}

        {error && <p className="text-xs text-danger">{error}</p>}
      </div>

      {/* Viewing another user from the admin dashboard: this is THEIR
          conversation, read from their account, and it is read-only -- a
          question typed here would be the admin's, not theirs. */}
      {viewingAs && (
        <p className="px-3 pt-2 text-[11px] text-muted-foreground">
          {t('orb.chat.viewingAs')}
        </p>
      )}
      <div className="px-3 py-2.5 border-t border-white/[0.06] flex items-center gap-2">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
          disabled={available === false || viewingAs}
          placeholder={viewingAs ? t('orb.chat.placeholder.readOnly') : available === false ? t('orb.chat.placeholder.unavailable') : t('orb.chat.placeholder.ask')}
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/60 disabled:opacity-50"
        />
        <button
          onClick={() => void send()}
          disabled={!draft.trim() || sending || available === false || viewingAs}
          className="text-primary disabled:text-muted-foreground/40 transition-colors"
          aria-label={t('orb.chat.send')}
        >
          <PaperPlaneRight size={18} weight="fill" />
        </button>
      </div>
      </>)}
    </div>
  );
}

export const NUDGE_NO_REASONS = ['notWhatINeeded', 'unclear', 'alreadyKnew', 'notNow'] as const;

/** "Did this help?" under a message the Orb raised on its own: yes, or no and why. */
function NudgeHelped({ turn, onAnswer }: { turn: Turn; onAnswer: (helped: string) => void }) {
  const { t } = useTranslation('learn');
  const trigger = turn.nudgeOf as string;
  if (turn.helped && turn.helped !== 'no') {
    return <p className="mt-1 text-[11px] text-muted-foreground">{t('orb.chat.rateThanks')}</p>;
  }
  const chip = 'rounded-full border border-border/60 px-2 py-0.5 text-[11px] hover:border-primary/40 transition-colors';
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5" role="group" aria-label={t('orb.helped.question')}>
      {turn.helped === 'no' ? (
        <>
          <span className="text-[11px] text-muted-foreground">{t('orb.helped.why')}</span>
          {NUDGE_NO_REASONS.map((r) => (
            <button key={r} type="button" className={chip} onClick={() => {
              track('assistant_live_feedback', trigger, undefined, { helped: false, reason: r });
              onAnswer(r);
            }}>{t(`orb.helped.reason.${r}`)}</button>
          ))}
        </>
      ) : (
        <>
          <span className="text-[11px] text-muted-foreground">{t('orb.helped.question')}</span>
          <button type="button" className={chip} onClick={() => { track('assistant_live_feedback', trigger, undefined, { helped: true }); onAnswer('yes'); }}>{t('orb.helped.yes')}</button>
          <button type="button" className={chip} onClick={() => onAnswer('no')}>{t('orb.helped.no')}</button>
        </>
      )}
    </div>
  );
}

export const BUILDER_ABANDON_REASONS = ['tooManySettings', 'unsureValues', 'justLooking', 'somethingBroke', 'later'] as const;

/** The second time the Builder was left halfway: why, from fixed choices (10-25). */
function AbandonReasons({ turn, onAnswer }: { turn: Turn; onAnswer: (reason: string) => void }) {
  const { t } = useTranslation('learn');
  if (turn.helped) return <p className="mt-1 text-[11px] text-muted-foreground">{t('orb.chat.rateThanks')}</p>;
  const chip = 'rounded-full border border-border/60 px-2 py-0.5 text-[11px] hover:border-primary/40 transition-colors';
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5" role="group" aria-label={t('orb.abandon.question')}>
      {BUILDER_ABANDON_REASONS.map((r) => (
        <button key={r} type="button" className={chip} onClick={() => {
          track('builder_abandon_reason', r);
          onAnswer(r);
        }}>{t(`orb.abandon.reason.${r}`)}</button>
      ))}
    </div>
  );
}

/** After a thumbs down: the guide for this screen, the answer again more simply, or a person. */
function StillStuck({ guide, handedOver, onGuide, onSimpler, onPerson }: {
  guide: string | null; handedOver: boolean;
  onGuide: (guide: string) => void; onSimpler: () => void; onPerson: () => void;
}) {
  const { t } = useTranslation('common');
  const chip = 'rounded-md border border-border/60 px-2 py-0.5 text-[11px] hover:bg-white/[0.06] transition-colors';
  return (
    <div className="mt-1.5 space-y-1">
      <p className="text-[11px] text-muted-foreground">{t('orb.stuck.title')}</p>
      {handedOver ? (
        <p className="text-[11px] text-primary">{t('orb.stuck.personSent')}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {guide && Object.prototype.hasOwnProperty.call(GUIDES, guide) && (
            <button type="button" className={chip} onClick={() => onGuide(guide)}>{t('orb.stuck.guide')}</button>
          )}
          <button type="button" className={chip} onClick={onSimpler}>{t('orb.stuck.simpler')}</button>
          <button type="button" className={chip} onClick={onPerson}>{t('orb.stuck.person')}</button>
        </div>
      )}
    </div>
  );
}
